// @ts-nocheck

import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Upload, Trash2, Image } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';

interface EventGalleryUploadProps {
  galleryImages: string[];
  onChange: (images: string[]) => void;
}

const EventGalleryUpload: React.FC<EventGalleryUploadProps> = ({
  galleryImages,
  onChange
}) => {
  const [isUploading, setIsUploading] = useState(false);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setIsUploading(true);

    try {
      const uploadPromises = Array.from(files).map(async (file) => {
        // Generate a unique filename
        const fileExt = file.name.split('.').pop();
        const fileName = `event-gallery-${Date.now()}-${Math.random().toString(36).substr(2, 9)}.${fileExt}`;
        
        // Upload file to event-gallery bucket
        const { data, error } = await supabase.storage
          .from('event-gallery')
          .upload(fileName, file, {
            cacheControl: '3600',
            upsert: false
          });

        if (error) throw error;

        // Get public URL
        const { data: urlData } = supabase.storage
          .from('event-gallery')
          .getPublicUrl(data.path);

        return urlData.publicUrl;
      });

      const uploadedUrls = await Promise.all(uploadPromises);
      const newGalleryImages = [...galleryImages, ...uploadedUrls];
      onChange(newGalleryImages);
      
      toast.success(`${uploadedUrls.length} image(s) uploaded successfully`);
    } catch (error) {
      console.error('Error uploading images:', error);
      toast.error('Failed to upload images');
    } finally {
      setIsUploading(false);
      // Reset the input value
      event.target.value = '';
    }
  };

  const handleDeleteImage = async (imageUrl: string, index: number) => {
    try {
      // Extract the file path from the URL if it's from our storage
      if (imageUrl.includes('event-gallery')) {
        const path = imageUrl.split('/event-gallery/')[1];
        if (path) {
          const { error } = await supabase.storage
            .from('event-gallery')
            .remove([path]);

          if (error) {
            console.error('Error deleting from storage:', error);
            // Continue with removing from UI even if storage deletion fails
          }
        }
      }

      // Remove from the array
      const newGalleryImages = galleryImages.filter((_, i) => i !== index);
      onChange(newGalleryImages);
      
      toast.success('Image removed successfully');
    } catch (error) {
      console.error('Error deleting image:', error);
      toast.error('Failed to delete image');
    }
  };

  return (
    <div className="space-y-4">
      <Label className="text-sm font-medium">Gallery Images</Label>
      
      {/* Upload Section */}
      <div className="flex items-center gap-2">
        <div className="relative">
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={handleFileUpload}
            disabled={isUploading}
            className="hidden"
            id="gallery-upload"
          />
          <Label htmlFor="gallery-upload" className="cursor-pointer">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={isUploading}
              asChild
            >
              <span>
                <Upload className="h-4 w-4 mr-2" />
                {isUploading ? 'Uploading...' : 'Upload Images'}
              </span>
            </Button>
          </Label>
        </div>
        <p className="text-xs text-gray-500">
          Select multiple images to add to the event gallery
        </p>
      </div>

      {/* Gallery Grid */}
      {galleryImages.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {galleryImages.map((imageUrl, index) => (
            <div key={index} className="relative group">
              <div className="aspect-square border rounded-lg overflow-hidden bg-gray-50">
                <img
                  src={imageUrl}
                  alt={`Gallery image ${index + 1}`}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.currentTarget.src = '/placeholder.svg';
                  }}
                />
              </div>
              <Button
                type="button"
                variant="destructive"
                size="sm"
                className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={() => handleDeleteImage(imageUrl, index)}
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {galleryImages.length === 0 && (
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
          <Image className="h-8 w-8 mx-auto mb-2 text-gray-400" />
          <p className="text-sm text-gray-500">No gallery images uploaded yet</p>
        </div>
      )}
    </div>
  );
};

export default EventGalleryUpload;